import Events from '@/shared/ipcUtils/Events'
import { onMounted, onUnmounted, ref } from 'vue'

type EventHandler = (...args: any[]) => void
type WindowTarget = 'other' | 'current' | 'all' | 'main' | number

const handlers = ref<Record<string, EventHandler[]>>({})

// Check if we're in the actual Electron environment by looking for process.type
const isElectron = () => {
  // Renderer process
  if (
    typeof window !== 'undefined' &&
    typeof window.process === 'object' &&
    window.process.type === 'renderer'
  ) {
    return true
  }

  // Main process
  if (
    typeof process !== 'undefined' &&
    typeof process.versions === 'object' &&
    !!process.versions.electron
  ) {
    return true
  }

  // Detect the user agent when the `nodeIntegration` option is set to true
  if (
    typeof navigator === 'object' &&
    typeof navigator.userAgent === 'string' &&
    navigator.userAgent.indexOf('Electron') >= 0
  ) {
    return true
  }

  return false
}

export const onEventBus = (event: string, handler: EventHandler) => {
  onMounted(() => {
    EventBus.on(event, handler)
  })

  onUnmounted(() => {
    EventBus.off(event, handler)
  })
}

// Single IPC listener for all events - only for Electron
if (isElectron()) {
  window.mainApi.receive(Events.EventBus.BROADCAST, (_, { event, payload }) => {
    if (handlers.value[event]) {
      handlers.value[event].forEach((handler) => handler(payload))
    }
  })
}

export const EventBus = {
  emit(event: string, payload = {}, target: WindowTarget = 'all') {
    try {
      if (isElectron()) {
        // Send event through IPC with target information
        window.mainApi.send(Events.EventBus.SEND, { event, payload, target })
      } else {
        // Direct handler execution for non-Electron environments
        if (handlers.value[event]) {
          handlers.value[event].forEach((handler) => handler(payload))
        }
      }
    } catch (e) {
      console.warn('Failed to emit event:', e)
    }
  },

  on(event: string, handler: EventHandler) {
    try {
      // Initialize handlers array if needed
      if (!handlers.value[event]) {
        handlers.value[event] = []
      }
      // Add the handler to the list
      handlers.value[event].push(handler)
    } catch (e) {
      console.warn('Failed to register event handler:', e)
    }
  },

  off(event: string, handler: EventHandler) {
    try {
      if (Array.isArray(handlers.value[event])) {
        const index = handlers.value[event].indexOf(handler)
        if (index > -1) {
          handlers.value[event].splice(index, 1)
          if (handlers.value[event].length === 0) {
            delete handlers.value[event]
          }
        }
      }
    } catch (e) {
      console.warn('Failed to remove event handler:', e)
    }
  }
}

// ─── Typed event wrappers ───────────────────────────────────────────────────
//
// EventBus itself stays string-keyed (it crosses Electron IPC and needs a
// dynamic shape), but the cross-feature events that anchor cache
// invalidation deserve named entry points so a typo doesn't silently
// disable a listener somewhere.

const EVENTS_CHANGED = 'events-changed'

/** Notify every feature subscribed to the global "calendar events
 *  changed" signal — defrag recomputes, inbox refreshes, badges
 *  update, etc. Prefer this over `EventBus.emit('events-changed', ...)`
 *  so the string lives in one place. */
export function emitEventsChanged(): void {
  EventBus.emit(EVENTS_CHANGED, {})
}

/** Subscribe to the "calendar events changed" signal. Returns the
 *  handler unchanged so callers can pair `onEventsChanged(h)` with
 *  `offEventsChanged(h)` symmetrically. */
export function onEventsChangedListener(handler: EventHandler): EventHandler {
  EventBus.on(EVENTS_CHANGED, handler)
  return handler
}

export function offEventsChangedListener(handler: EventHandler): void {
  EventBus.off(EVENTS_CHANGED, handler)
}
